import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { COOKIE_SESSION_TOKEN, api, type PublicAccount } from "./api.js";

/**
 * Legacy key that used to hold a raw bearer in `localStorage`. Any browser that
 * still carries one has it cleared on first load: a session credential readable
 * by page JavaScript is exactly what the cookie transport removes.
 */
const LEGACY_TOKEN_KEY = "smart_shikka_web_token";

export interface AuthState {
  token: string | null;
  account: PublicAccount | null;
  ready: boolean;
  isOwner: boolean;
  /** Owner OR the single super-sub-admin (daughter) — the Shikka CEO-eligible roles. */
  isPrivileged: boolean;
  setSession: (account: PublicAccount) => void;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [account, setAccount] = useState<PublicAccount | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    try {
      localStorage.removeItem(LEGACY_TOKEN_KEY);
    } catch {
      /* storage unavailable */
    }
  }, []);

  // The session cookie is HttpOnly, so the only way to learn whether a session
  // exists is to ask the server.
  const refresh = useCallback(async () => {
    const res = await api.session(COOKIE_SESSION_TOKEN);
    setAccount(res.ok && res.data?.account ? res.data.account : null);
    setReady(true);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const setSession = useCallback((nextAccount: PublicAccount) => {
    setAccount(nextAccount);
    setReady(true);
  }, []);

  const logout = useCallback(async () => {
    // Server-side deletion + cookie clearing; nothing to erase locally.
    await api.logout(COOKIE_SESSION_TOKEN);
    setAccount(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      token: account ? COOKIE_SESSION_TOKEN : null,
      account,
      ready,
      isOwner: account?.role === "super-admin" && account?.status === "active",
      isPrivileged:
        (account?.role === "super-admin" || account?.role === "super-sub-admin") &&
        account?.status === "active",
      setSession,
      refresh,
      logout,
    }),
    [account, ready, setSession, refresh, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
